import { useState, useEffect } from 'react'
import { CAMPAIGN_TYPE_OPTIONS } from '../constants/campaignTypes'
import { getCampaigns, DashboardFilters } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { ExportButton } from '../components/ExportButton'
import CappingCell from '../components/CappingCell'

interface Campaign {
  name: string
  totalRecords: number
  totalLeads: number
  totalRevenue: number
  validationRate: number
  cpl: number
  capping: number | null
  status: string
}

interface CampaignsDetailsData {
  campaigns: Campaign[]
  total: number
}

const CampaignsDetails = () => {
  const [data, setData] = useState<CampaignsDetailsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(25)
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    source: 'all',
    type: 'all',
    flow: 'all'
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      
      const apiFilters: DashboardFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        type: filters.type,
        flow: filters.flow
      }
      
      const response = await getCampaigns(apiFilters)
      setData(response.data)
      setError(null)
      
      console.log('🎯 Campaigns Details data loaded:', response.data)
    } catch (err) {
      setError('Error al cargar los detalles de campañas')
      console.error('Error fetching campaigns details:', err)
    } finally {
      setLoading(false)
    }
  }

  const resetAllFilters = () => {
    console.log('Reseteando todos los filtros')
    const defaultFilters = {
      startDate: '2025-09-01',
      endDate: '2025-11-30',
      source: 'all',
      type: 'all',
      flow: 'all'
    }
    setFilters(defaultFilters)
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filtering
  }

  // Actualiza el capping en memoria tras guardarlo (evita recargar toda la
  // lista solo por editar un campo).
  const handleCappingSaved = (campaignName: string, newValue: number | null) => {
    setData(prev =>
      prev
        ? {
            ...prev,
            campaigns: prev.campaigns.map(c =>
              c.name === campaignName ? { ...c, capping: newValue } : c
            ),
          }
        : prev
    )
  }

  // Pagination logic
  const totalPages = data ? Math.ceil(data.campaigns.length / itemsPerPage) : 0
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentCampaigns = data ? data.campaigns.slice(startIndex, endIndex) : []

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <div>No hay datos disponibles</div>
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">🎯</span>
            CAMPAIGNS DETAILS
          </h1>
          <p className="text-dashboard-muted mt-1">
            Análisis detallado de campañas LC
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ExportButton 
            type="campaigns"
            filters={filters}
          />
          <div className="text-right">
            <div className="text-sm text-dashboard-muted">Período</div>
            <div className="text-sm font-semibold text-dashboard-text">
              {filters.startDate} - {filters.endDate}
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filter-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-dashboard-text">Filtros de Análisis</h3>
          <button 
            onClick={resetAllFilters}
            className="btn-secondary text-sm"
          >
            Resetear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              SOURCE
            </label>
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="filter-select w-full"
            >
              <option value="all">Todas las fuentes</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="microsoft">Microsoft</option>
              <option value="tiktok">TikTok</option>
              <option value="leadcentre">Leadcentre</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              TYPE
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="filter-select w-full"
            >
              {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Flow
            </label>
            <select
              value={filters.flow}
              onChange={(e) => handleFilterChange('flow', e.target.value)}
              className="filter-select w-full"
            >
              <option value="all">Todos los flows</option>
              <option value="seguro-salud">Seguro Salud</option>
              <option value="creditos-personales">Créditos Personales</option>
              <option value="tarjeta-credito">Tarjeta de Crédito</option>
              <option value="seguro-decesos">Seguro Decesos</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="filter-select w-full"
            />
          </div>
        </div>
      </div>

      {/* LC CAMPAIGNS Table */}
      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text flex items-center justify-between">
            <span className="flex items-center">
              <span className="mr-2">📋</span>
              LC CAMPAIGNS
            </span>
            <span className="text-sm font-normal text-dashboard-muted">
              {startIndex + 1} - {Math.min(endIndex, data.campaigns.length)} de {data.campaigns.length}
            </span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>#</th>
                <th>TYPE</th>
                <th>client_campaign</th>
                <th>source</th>
                <th>payout</th>
                <th>validation_rate</th>
                <th>Capping</th>
                <th>number_leads</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {currentCampaigns.map((campaign, index) => (
                <tr key={index}>
                  <td className="font-semibold">{startIndex + index + 1}</td>
                  <td>
                    <span className={`badge ${
                      campaign.name.includes('CLICKOUT') ? 'badge-info' :
                      campaign.name.includes('SPONSOR') ? 'badge-success' :
                      campaign.name.includes('COREGISTRO') ? 'badge-warning' :
                      campaign.name.includes('MARKETPLACE') ? 'badge-premium' :
                      'badge-error'
                    }`}>
                      {campaign.name.includes('CLICKOUT') ? 'CLICKOUT' :
                       campaign.name.includes('SPONSOR') ? 'SPONSOR' :
                       campaign.name.includes('COREGISTRO') ? 'COREGISTRO' :
                       campaign.name.includes('MARKETPLACE') ? 'MARKETPLACE' :
                       'OTHER'}
                    </span>
                  </td>
                  <td className="max-w-xs truncate" title={campaign.name}>
                    {campaign.name}
                  </td>
                  <td>
                    <span className={`badge ${
                      campaign.name.includes('Google') ? 'badge-info' :
                      campaign.name.includes('Facebook') ? 'badge-info' :
                      campaign.name.includes('Microsoft') ? 'badge-warning' :
                      campaign.name.includes('TikTok') ? 'badge-error' :
                      campaign.name.includes('Madz') || campaign.name.includes('Adclick') ? 'badge-premium' :
                      'badge-secondary'
                    }`}>
                      {campaign.name.includes('Google') ? 'Google' :
                       campaign.name.includes('Facebook') ? 'Facebook' :
                       campaign.name.includes('Microsoft') ? 'Microsoft' :
                       campaign.name.includes('TikTok') ? 'TikTok' :
                       campaign.name.includes('Madz') || campaign.name.includes('Adclick') ? 'Leadcentre' :
                       'Other'}
                    </span>
                  </td>
                  <td className="font-semibold">€{campaign.cpl.toFixed(2)}</td>
                  <td>
                    <div className="flex items-center">
                      <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${campaign.validationRate * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm">{(campaign.validationRate * 100).toFixed(1)}%</span>
                    </div>
                  </td>
                  <td>
                    <CappingCell
                      campaignName={campaign.name}
                      value={campaign.capping}
                      onSaved={handleCappingSaved}
                    />
                  </td>
                  <td className="font-semibold">{campaign.totalLeads.toLocaleString()}</td>
                  <td className="font-semibold text-green-600">€{campaign.totalRevenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-6 border-t border-dashboard-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-dashboard-muted">
                Mostrando {startIndex + 1} a {Math.min(endIndex, data.campaigns.length)} de {data.campaigns.length} campañas
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Anterior
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i
                    if (pageNum > totalPages) return null
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded ${
                          currentPage === pageNum
                            ? 'bg-globo-500 text-white'
                            : 'bg-white text-dashboard-text hover:bg-dashboard-card-hover'
                        } border border-dashboard-border`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card text-center p-6">
          <div className="text-2xl mb-2">📊</div>
          <div className="text-sm text-dashboard-muted mb-1">Total Campaigns</div>
          <div className="text-2xl font-bold text-dashboard-text">
            {data.campaigns.length}
          </div>
        </div>
        <div className="card text-center p-6">
          <div className="text-2xl mb-2">👥</div>
          <div className="text-sm text-dashboard-muted mb-1">Total Leads</div>
          <div className="text-2xl font-bold text-dashboard-text">
            {data.campaigns.reduce((sum, c) => sum + c.totalLeads, 0).toLocaleString()}
          </div>
        </div>
        <div className="card text-center p-6">
          <div className="text-2xl mb-2">💰</div>
          <div className="text-sm text-dashboard-muted mb-1">Total Revenue</div>
          <div className="text-2xl font-bold text-green-600">
            €{data.campaigns.reduce((sum, c) => sum + c.totalRevenue, 0).toFixed(2)}
          </div>
        </div>
        <div className="card text-center p-6">
          <div className="text-2xl mb-2">✅</div>
          <div className="text-sm text-dashboard-muted mb-1">Avg Validation</div>
          <div className="text-2xl font-bold text-dashboard-text">
            {(data.campaigns.reduce((sum, c) => sum + c.validationRate, 0) / data.campaigns.length * 100).toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  )
}

export default CampaignsDetails
